import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select';
import { cn } from '../../lib/utils';

export default function SelectNumberField({
	label,
	options,
	className,
	orientation,
	placeholder,
}: {
	label: string;
	options: { value: number; label: string }[];
	className?: string;
	orientation?: 'vertical' | 'horizontal';
	placeholder?: string;
}) {
	const field = useFieldContext<number | null>();
	const formReadOnly = useFormReadOnly();
	const isHorizontal = orientation === 'horizontal';
	const isOptional = useIsFieldOptional(field.name);

	return (
		<div
			className={cn(
				className,
				'[&_[data-slot=select-trigger]]:transition-[color,border-color,padding,box-shadow]',
				formReadOnly &&
					'pointer-events-none [&_[data-slot=select-trigger]]:border-transparent [&_[data-slot=select-trigger]]:px-0 [&_[data-slot=select-trigger]]:focus-visible:ring-0 [&_[data-slot=select-trigger]>svg]:hidden',
			)}
		>
			<div className={isHorizontal ? 'flex items-start gap-3' : 'grid gap-2'}>
				{label && (
					<FieldLabelRow
						label={label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
						className={cn('flex items-center justify-between', isHorizontal && 'w-36 shrink-0')}
					/>
				)}
				<div className={isHorizontal ? 'flex-1' : undefined}>
					<Select
						value={field.state.value?.toString() ?? ''}
						onValueChange={(val) => field.handleChange(val === '' ? null : Number(val))}
					>
						<SelectTrigger id={field.name} aria-invalid={hasFieldError(field.state.meta)} className="w-full">
							<SelectValue placeholder={placeholder ?? label} />
						</SelectTrigger>
						<SelectContent>
							{options.map((option) => (
								<SelectItem key={option.value} value={option.value.toString()}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<FieldInfo fieldState={field.state} />
				</div>
			</div>
		</div>
	);
}
